import { useState, useEffect } from 'react';
import { api } from './lib/api';

interface CloturePreview {
  exercice_id: string;
  entries_count: number;
  entries_to_lock: number;
  draft_count: number;
  uncategorized_count: number;
  validation: {
    all_categorized: boolean;
    no_drafts_with_issues: boolean;
    tva_balanced: boolean;
  };
  new_exercice: { debut: string; fin: string };
  opening_balances: Array<{
    compte_id: string;
    libelle: string;
    solde_centimes: number;
  }>;
}

interface Props {
  entrepriseId: string;
  exerciceId: string;
  onComplete?: (newExerciceId: string) => void;
}

type WizardStep = 1 | 2 | 3 | 4;

const stepLabels = ['Validation', 'Verrouillage', 'Confirmation', 'Terminé'];

export default function ClotureWizard({ entrepriseId, exerciceId, onComplete }: Props) {
  const [step, setStep] = useState<WizardStep>(1);
  const [preview, setPreview] = useState<CloturePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [result, setResult] = useState<any>(null);

  useEffect(() => {
    loadPreview();
  }, [entrepriseId, exerciceId]);

  const loadPreview = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.exercices.cloturePreview(entrepriseId, exerciceId);
      setPreview(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const executeCloture = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.exercices.cloture(entrepriseId, exerciceId);
      setResult(data);
      setStep(4);
      if (onComplete) onComplete(data.new_exercice_id);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !preview) return <p className="text-sm text-gray-500">Chargement...</p>;
  if (error && !preview) return <div className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-lg text-sm">Erreur : {error}</div>;
  if (!preview) return null;

  return (
    <div className="max-w-3xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Clôture de l'exercice</h2>

      <div className="flex items-center justify-between mb-8">
        {stepLabels.map((label, i) => {
          const s = i + 1;
          const isActive = step >= s;
          return (
            <div key={s} className={`flex items-center gap-2 ${isActive ? 'text-blue-600 font-semibold' : 'text-gray-400'}`}>
              <div className={`w-7 h-7 rounded-full text-sm flex items-center justify-center font-bold ${isActive ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                {s}
              </div>
              <span className="text-sm hidden sm:inline">{label}</span>
            </div>
          );
        })}
      </div>

      {error && <div className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-lg text-sm mb-4">{error}</div>}

      {step === 1 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">1. Validation pré-clôture</h3>
          <div className="overflow-hidden rounded-lg border border-gray-100">
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b border-gray-100">
                  <td className="px-4 py-3 text-gray-700">Écritures dans l'exercice</td>
                  <td className="px-4 py-3 font-bold text-gray-900">{preview.entries_count}</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="px-4 py-3 text-gray-700">Écritures à verrouiller</td>
                  <td className="px-4 py-3 font-bold text-gray-900">{preview.entries_to_lock}</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="px-4 py-3 text-gray-700">Toutes les écritures catégorisées</td>
                  <td className="px-4 py-3">{preview.validation.all_categorized ? <span className="text-green-600">✓ Oui</span> : <span className="text-red-600">✗ {preview.uncategorized_count} non catégorisée(s)</span>}</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="px-4 py-3 text-gray-700">Aucun brouillon en attente</td>
                  <td className="px-4 py-3">{preview.validation.no_drafts_with_issues ? <span className="text-green-600">✓ Oui</span> : <span className="text-red-600">✗ {preview.draft_count} brouillon(s)</span>}</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-gray-700">TVA équilibrée</td>
                  <td className="px-4 py-3">{preview.validation.tva_balanced ? <span className="text-green-600">✓ Oui</span> : <span className="text-red-600">✗ Non</span>}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="flex justify-end mt-6">
            <button onClick={() => setStep(2)} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">Suivant</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">2. Verrouillage des écritures</h3>
          <div className="text-amber-700 bg-amber-50 border border-amber-200 px-4 py-3 rounded-lg text-sm mb-4">
            ⚠ Attention : le verrouillage est irréversible (Code de commerce Art. L123-22).
          </div>
          <p className="text-sm text-gray-700">
            {preview.entries_to_lock} écriture(s) seront verrouillées.
            Aucune modification ne sera possible après la clôture.
          </p>
          <div className="flex justify-between mt-6">
            <button onClick={() => setStep(1)} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors">Retour</button>
            <button onClick={() => setStep(3)} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">Suivant</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">3. Confirmation et report des soldes</h3>
          <p className="text-sm text-gray-700 mb-4">
            <strong>Nouvel exercice :</strong> {preview.new_exercice.debut} → {preview.new_exercice.fin}
          </p>
          {preview.opening_balances.length > 0 && (
            <>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Soldes à reporter (classes 1-5)</h4>
              <div className="overflow-hidden rounded-lg border border-gray-100 mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Compte</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Libellé</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Solde (€)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.opening_balances.map((b) => (
                      <tr key={b.compte_id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-gray-700 border-b border-gray-100 font-mono text-xs">{b.compte_id}</td>
                        <td className="px-4 py-3 text-gray-700 border-b border-gray-100">{b.libelle}</td>
                        <td className="px-4 py-3 text-gray-700 border-b border-gray-100 text-right">{(b.solde_centimes / 100).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <label className="flex items-center gap-2 text-sm text-gray-700 mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Je confirme vouloir clôturer cet exercice. Cette action est irréversible.
          </label>
          <div className="flex justify-between">
            <button onClick={() => setStep(2)} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors">Retour</button>
            <button onClick={executeCloture} disabled={!confirmed || loading} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {loading ? 'Clôture en cours...' : 'Clôturer l\'exercice'}
            </button>
          </div>
        </div>
      )}

      {step === 4 && result && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">4. Clôture terminée</h3>
          <div className="text-green-700 bg-green-50 border border-green-200 px-4 py-3 rounded-lg text-sm">
            <p className="font-medium">✓ Exercice clôturé avec succès</p>
            <p className="mt-1">{result.entries_locked} écriture(s) verrouillée(s)</p>
            <p>Nouvel exercice créé : {result.new_exercice_id}</p>
          </div>
        </div>
      )}
    </div>
  );
}
